import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { Eye, Compass, Shield, Flame, ArrowRight, Timer, Brain, Fingerprint, AlertTriangle, Smartphone } from 'lucide-react';
import { playerTypes, rootCodes } from '../data/resultsData';
import type { PlayerType, RootCode } from '../data/resultsData';
const iconMap = {
  Eye,
  Compass,
  Shield,
  Flame
};
const rootCodeIconMap: Record<RootCode, React.ElementType> = {
  MENTE: Brain,
  TIEMPO: Timer,
  IDENTIDAD: Fingerprint,
  MIEDO: AlertTriangle,
  DISTRACCION: Smartphone
};
const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12
    }
  }
};
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
interface ResultsPageProps {
  playerType: PlayerType;
  rootCode: RootCode;
  onViewOffer: () => void;
}
export function ResultsPage({
  playerType,
  rootCode,
  onViewOffer
}: ResultsPageProps) {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const typeData = playerTypes[playerType];
  const codeData = rootCodes[rootCode];
  const TypeIcon = iconMap[typeData.iconName];
  const CodeIcon = rootCodeIconMap[rootCode];
  return <div className="min-h-screen bg-matrix-bg px-5 py-12 md:py-20" data-id="element-339">
      <motion.div initial="hidden" animate="visible" variants={stagger} className="mx-auto max-w-2xl" data-id="element-340">
        {/* Pre-headline */}
        <motion.p variants={fadeUp} className="font-body text-sm text-matrix-gold-muted text-center" data-id="element-341">
          Tu resultado está listo.
        </motion.p>

        {/* Type reveal */}
        <motion.div variants={fadeUp} className="mt-6 text-center" data-id="element-342">
          <motion.div initial={{
          scale: 0,
          rotate: -20
        }} animate={{
          scale: 1,
          rotate: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 15,
          delay: 0.3
        }} className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-2xl border border-matrix-gold/30 bg-gradient-to-br from-matrix-gold/10 to-matrix-gold/5 shadow-[0_0_40px_rgba(201,168,76,0.12)]" data-id="element-343">
            <TypeIcon className="h-10 w-10 text-matrix-gold" data-id="element-344" />
          </motion.div>

          <h1 className="font-heading text-2xl font-bold text-matrix-text md:text-4xl" data-id="element-345">
            Tu Tipo de Jugador es:
          </h1>
          <motion.h2 initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.5,
          duration: 0.6
        }} className="mt-2 font-heading text-3xl font-bold text-matrix-gold md:text-5xl" data-id="element-346">
            {typeData.title}
          </motion.h2>
          <p className="mt-2 font-body text-sm text-matrix-text-muted italic" data-id="element-347">
            {typeData.subtitle}
          </p>
        </motion.div>

        {/* Subheadline */}
        <motion.p variants={fadeUp} className="mt-6 text-center font-body text-base leading-relaxed text-matrix-text-muted" data-id="element-348">
          Esto explica por qué estás viendo la Matrix… pero todavía sientes
          ruido, repetición o cansancio. La buena noticia:{' '}
          <span className="text-matrix-text font-medium" data-id="element-349">no estás roto/a</span>.
          Estabas ejecutando un programa.
        </motion.p>

        {/* Type detail card */}
        <motion.div variants={fadeUp} className="mt-10 rounded-2xl border border-matrix-border/50 bg-matrix-card/60 p-6 md:p-8" data-id="element-350">
          {/* Validation */}
          <div className="mb-6" data-id="element-351">
            <h3 className="font-heading text-lg font-semibold text-matrix-text mb-2" data-id="element-352">
              Lo que esto significa
            </h3>
            <p className="font-body text-sm leading-relaxed text-matrix-text-muted" data-id="element-353">
              {typeData.validation}
            </p>
          </div>

          {/* Blind spot */}
          <div className="mb-6 rounded-xl border border-matrix-gold/15 bg-matrix-gold/5 p-5" data-id="element-354">
            <h3 className="font-heading text-base font-semibold text-matrix-gold mb-2" data-id="element-355">
              Tu Punto Ciego
            </h3>
            <p className="font-body text-sm leading-relaxed text-matrix-text-muted" data-id="element-356">
              {typeData.blindSpot}
            </p>
            <p className="mt-2 font-body text-sm text-matrix-text-muted" data-id="element-357">
              Tu programa favorito es:{' '}
              <span className="font-medium text-matrix-text italic" data-id="element-358">
                {typeData.favoriteProgram}
              </span>
            </p>
          </div>

          {/* Signal */}
          <div className="rounded-xl border border-matrix-border/40 bg-matrix-bg/50 p-5" data-id="element-359">
            <h3 className="font-body text-xs font-semibold uppercase tracking-wider text-matrix-text-dim mb-2" data-id="element-360">
              Señal de que es verdad
            </h3>
            <p className="font-heading text-sm italic leading-relaxed text-matrix-text/90" data-id="element-361">
              "{typeData.signal}"
            </p>
          </div>
        </motion.div>

        {/* Root code */}
        <motion.div variants={fadeUp} className="mt-8 rounded-2xl border border-matrix-gold/20 bg-gradient-to-br from-matrix-card to-matrix-bg p-6 md:p-8" data-id="element-362">
          <div className="flex items-center gap-3 mb-4" data-id="element-363">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-matrix-gold/20 bg-matrix-gold/5" data-id="element-364">
              <CodeIcon className="h-5 w-5 text-matrix-gold" data-id="element-365" />
            </div>
            <div data-id="element-366">
              <p className="font-body text-xs text-matrix-text-dim uppercase tracking-wider" data-id="element-367">
                Tu Código Raíz ahora mismo
              </p>
              <h3 className="font-heading text-xl font-bold text-matrix-gold" data-id="element-368">
                {codeData.name}
              </h3>
            </div>
          </div>
          <p className="font-body text-sm leading-relaxed text-matrix-text-muted" data-id="element-369">
            Esto es la palanca principal. Cuando mueves este código, lo demás
            empieza a ordenarse.
          </p>
          <p className="mt-3 font-heading text-base italic text-matrix-text/90" data-id="element-370">
            "{codeData.description}"
          </p>
        </motion.div>

        {/* Mini-plan gratis */}
        <motion.div variants={fadeUp} className="mt-8 rounded-2xl border border-matrix-border/50 bg-matrix-card/40 p-6 md:p-8" data-id="element-371">
          <h3 className="font-heading text-lg font-semibold text-matrix-text mb-1" data-id="element-372">
            Tu primer paso (hoy, en 2 minutos)
          </h3>
          <p className="font-body text-sm text-matrix-gold mb-5" data-id="element-373">
            "El Cambio de Cámara"
          </p>

          <ol className="space-y-4" data-id="element-374">
            {['Pon un temporizador de 120 segundos.', 'Di por dentro: "Yo soy el jugador, no el personaje."', 'Nota 3 cosas:\n• Qué siente tu cuerpo ahora\n• Qué pensamiento se repite\n• Qué emoción está debajo', 'Termina con: "Elijo una decisión pequeña, pero real, hoy."'].map((step, i) => <li key={i} className="flex gap-3" data-id="element-375">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-matrix-gold/30 bg-matrix-gold/10 font-body text-xs font-semibold text-matrix-gold" data-id="element-376">
                  {i + 1}
                </span>
                <p className="font-body text-sm leading-relaxed text-matrix-text-muted whitespace-pre-line pt-0.5" data-id="element-377">
                  {step}
                </p>
              </li>)}
          </ol>

          <p className="mt-6 font-body text-sm italic text-matrix-text-muted" data-id="element-378">
            Si sentiste aunque sea 5% más espacio interno, eso es prueba de que
            tu consciencia puede reescribir el juego.
          </p>
        </motion.div>

        {/* Bridge to offer */}
        <motion.div variants={fadeUp} className="mt-12 text-center" data-id="element-379">
          <h3 className="font-heading text-xl font-semibold text-matrix-text md:text-2xl" data-id="element-380">
            Tu plan completo está listo
          </h3>
          <p className="mt-2 font-body text-sm text-matrix-text-muted" data-id="element-381">
            (y fue hecho para tu tipo)
          </p>

          <p className="mx-auto mt-4 max-w-md font-body text-sm leading-relaxed text-matrix-text-muted" data-id="element-382">
            Según tus respuestas, lo que más te va a servir ahora no es
            "motivación", sino un manual que te guíe con estructura.
          </p>

          <button onClick={onViewOffer} className="group mt-8 inline-flex items-center gap-2.5 rounded-lg bg-gradient-to-r from-matrix-gold to-matrix-gold-bright px-8 py-4 font-body text-sm font-semibold uppercase tracking-wider text-matrix-bg shadow-[0_0_32px_rgba(201,168,76,0.2)] transition-all duration-300 hover:shadow-[0_0_48px_rgba(201,168,76,0.35)] hover:scale-[1.02] active:scale-[0.98]" data-id="element-383">
            Ver mi plan recomendado
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" data-id="element-384" />
          </button>

          <p className="mt-4 font-body text-xs text-matrix-text-dim" data-id="element-385">
            Acceso inmediato &bull; Léelo en cualquier dispositivo &bull;
            Garantía de devolución
          </p>
        </motion.div>
      </motion.div>
    </div>;
}